import { Context } from "@nuxt/types";
import { type Ref, unref } from "@nuxtjs/composition-api";
import { type MetaInfo, type MetaPropertyProperty } from "vue-meta";
import { type RouteMeta } from "vue-router";

import { $config } from "@/utils/accessors/config";
import normalizePath from "@/utils/transform/normalize-path";

interface MetaInfoWithBranchMeta {
  title?: string | Ref<string>;
  customTitle?: boolean;
  description?: string | Ref<string>;
  noIndex?: boolean;
  noFollow?: boolean;
  branchMeta?: {
    // keys to set branch banner data from ltk-vue, used primarily for translations.
    title: string;
    subtitle: string;
    avatarUrl?: string;
    button: string;
    getButton: string;
    openButton: string;
    page: string;
    continue: string;
    // keys specific to new banner copy being tested
    title_treatment: string;
    subtitle_treatment: string;
    button_treatment: string;
  };
}

export default class MetaService {
  hostname: string;
  context: Context;
  vid: string;

  constructor(context: Context) {
    this.context = context;
    this.hostname = `https://${$config.ltkSite}`;
    this.vid = context?.$branch?.vid || "fallback-vid-meta";
  }

  truncateString(input: string, maxLength: number) {
    let output = input ?? "";
    if (output.includes("|")) {
      output = output.substr(0, output.indexOf("|")).trim();
    }

    // truncate product name if longer than maxLength
    if (output.length > maxLength) {
      output = output.substr(0, maxLength);

      // re-trim if we are in the middle of a word
      output = `${output.substr(0, Math.min(output.length, output.lastIndexOf(" ")))} …`;
    }
    return output.trim();
  }

  setMetaName(name: string, content: string) {
    return {
      hid: name,
      name,
      content,
    };
  }

  setMetaProp(prop: string, content: string) {
    return {
      hid: prop,
      property: prop,
      content,
    };
  }

  /**
   * Since link collection items cannot be deduplicated with a `hid` property, this method needs
   * to be able to find and execute any path overrides defined on each route.
   */
  setCanonical(route: Context["route"]) {
    const href = this.getCanonicalUrl(route);
    return [{ rel: "canonical", href }];
  }

  getCanonicalUrl(route: Context["route"]) {
    const meta = (route.meta as RouteMeta[] | undefined)?.[0];
    const path = normalizePath(meta?.canonicalPath?.(route) ?? route.path);
    return `${this.hostname}${path}`;
  }

  setDescription(description = "") {
    // description
    if (description) {
      return [this.setMetaName("description", description), this.setMetaProp("og:description", description)];
    }
    return [];
  }

  setMobileAppMeta(path: string, os: string) {
    const platform = `al:${os}`;
    let store;
    let url;

    if (os === "ios") {
      url = `com.rewardstyle.ltk:/${path}`;
      store = this.setMetaProp(`${platform}:app_store_id`, "1154027990");
    } else {
      url = `com.rewardstyle.liketoknowit:/${path}`;
      store = this.setMetaProp(`${platform}:package`, "com.rewardstyle.liketoknowit");
    }

    return [this.setMetaProp(`${platform}:url`, url), this.setMetaProp(`${platform}:app_name`, "LTK"), store];
  }

  setOGImg(imgUrl: string, width: string | number, height = width) {
    return [
      this.setMetaProp("og:image", imgUrl),
      this.setMetaProp("og:image:secure_url", imgUrl),
      this.setMetaProp("og:image:width", `${width}`),
      this.setMetaProp("og:image:height", `${height}`),
      this.setMetaName("twitter:card", "summary_large_image"),
    ];
  }

  setPageMeta({
    title = "",
    customTitle = false,
    description = "",
    noIndex = false,
    noFollow = false,
    branchMeta,
  }: MetaInfoWithBranchMeta): MetaInfo {
    this.vid = this.context?.$branch?.vid || "fallback-vid-page-meta";
    const head: MetaInfo = Object.assign({}, this.setTitle(unref(title), customTitle));
    const meta = head.meta || [];

    // description
    meta.push(...this.setDescription(unref(description)));

    // noindex nofollow
    if (noIndex && noFollow) {
      meta.push(this.setMetaName("robots", "noindex, nofollow"));
    } else if (noIndex) {
      meta.push(this.setMetaName("robots", "noindex"));
    }

    const deeplinkPath = normalizePath(this.context.route.fullPath);
    const deeplinkPathWithVid = this.context.$branch?.appendVidToUrl
      ? this.context.$branch.appendVidToUrl(normalizePath(this.context.route.fullPath))
      : normalizePath(this.context.route.fullPath);
    const trimmedDeeplinkPath = this.context.$branch?.appendVidToUrl
      ? this.context.$branch.appendVidToUrl(deeplinkPath.substr(1))
      : deeplinkPath.substr(1);

    // og:url
    const canonicalUrl = this.getCanonicalUrl(this.context.route);
    meta.push(this.setMetaProp("og:url", canonicalUrl));

    // mobile app deeplink
    meta.push(...this.setMobileAppMeta(deeplinkPathWithVid, "ios"));
    meta.push(...this.setMobileAppMeta(deeplinkPathWithVid, "android"));

    // branch journey deep link
    meta.push(
      this.setMetaProp("branch:deeplink:$deeplink_path", deeplinkPathWithVid),
      this.setMetaProp("branch:deeplink:app_url", trimmedDeeplinkPath),
    );
    if (branchMeta) {
      // These keys let us set information for the branch banners such as button or title text
      // the _treatment keys are for usage in experiments when new copy is being tested.
      meta.push(
        this.setMetaProp("branch:deeplink:title", branchMeta?.title),
        this.setMetaProp("branch:deeplink:subtitle", branchMeta?.subtitle),
        this.setMetaProp("branch:deeplink:button", branchMeta?.button),
        this.setMetaProp("branch:deeplink:getButton", branchMeta?.getButton),
        this.setMetaProp("branch:deeplink:openButton", branchMeta?.openButton),
        this.setMetaProp("branch:deeplink:page", branchMeta?.page),
        this.setMetaProp("branch:deeplink:continue", branchMeta?.continue),
        this.setMetaProp("branch:deeplink:title_treatment", branchMeta?.title_treatment),
        this.setMetaProp("branch:deeplink:subtitle_treatment", branchMeta?.subtitle_treatment),
        this.setMetaProp("branch:deeplink:button_treatment", branchMeta?.button_treatment),
      );
      if (branchMeta.avatarUrl) {
        meta.push(this.setMetaProp("branch:deeplink:avatarUrl", branchMeta?.avatarUrl));
      }
    }

    // canonical link
    head.link = this.setCanonical(this.context.route);

    return head;
  }

  setSiteMeta(context: Context) {
    const faviconSize = 512;

    const head: MetaInfo = {
      meta: [
        this.setMetaProp("fb:app_id", "258637720963039"),
        ...this.setOGImg(`${this.hostname}${context.$icon(faviconSize)}`, `${faviconSize}`),
      ],
    };

    return head;
  }

  setTitle(title = "", customTitle = false) {
    const meta: MetaInfo["meta"] = [];
    const head: MetaInfo = {
      meta: [],
    };

    // page title
    if (title) {
      const ogTitle: MetaPropertyProperty = this.setMetaProp("og:title", title);

      head.title = title;

      if (customTitle) {
        head.titleTemplate = "";
        ogTitle.template = (s: string) => s;
      }

      meta.push(ogTitle);
    }

    head.meta = meta;
    return head;
  }
}
